import { resolveBonneReponse } from './questionUtils';

/**
 * Rejouer un quiz en ligne (décision du fondateur, 04/10/2026) : un rejeu
 * TERMINÉ est un défi (un des 5 crédités du jour, barème des défis), un rejeu
 * abandonné ne compte pas ; le back en est juge (`defis-quotidiens/submit/`
 * avec `session_id`). La copie et la note officielles ne bougent jamais.
 *
 * Le rejeu s'ouvre dès que la copie officielle est rendue, clôturé ou pas
 * (décision du prof, 05/10, épreuve de Musk : une seule condition pour
 * l'élève, aucun geste caché pour le prof). Sa correction en direct ne devance
 * jamais celle du prof : une question retirée au sort a d'autres nombres et se
 * corrige en entier ; une question reprise à l'identique (écrite à la main, ou
 * tirage échoué) ne dit que juste ou faux tant que le prof masque la
 * correction, sans la réponse attendue.
 */

/** La copie officielle de l'élève : sa ligne dans `eleves_reponses` de la séance. */
const copieDe = (session, eleveId) => (session?.eleves_reponses || [])
    .find((e) => String(e.id_eleve) === String(eleveId));

/** Les réponses de la copie officielle, à plat (les parties de `reponses`). */
const reponsesDe = (session, eleveId) => {
    const parties = copieDe(session, eleveId)?.reponses;
    if (!parties || typeof parties !== 'object') return [];
    return Object.values(parties).flatMap((p) => (p && typeof p === 'object' ? Object.values(p) : []));
};

/** Une copie rendue porte au moins une question : une copie remise à zéro par le
 * professeur ne compte pas (même règle que le back, `_copie_rendue`). */
export const copieRendue = (session, eleveId) => reponsesDe(session, eleveId).length > 0;

/** « Rejouer » s'offre sur un quiz en ligne dont la copie est rendue. */
export const peutRejouer = (quiz, eleveId) => quiz?.source === 'session'
    && quiz.mode_passation === 'en_ligne'
    && copieRendue(quiz, eleveId);

/** Le premier essai, lu dans la copie officielle : ses bonnes réponses, sur les questions du quiz. */
export const premierEssai = (session, eleveId) => ({
    bonnes: reponsesDe(session, eleveId).filter((r) => r && r.score === 1).length,
    total: (session?.questions || []).length,
});

/** Une question de générateur retirée au sort (l'original si le tirage échoue, jamais de trou) ;
 * une question écrite à la main (sans generator_key) reprise telle quelle. `retiree` : d'autres nombres. */
const retirerAuSort = (q, generateExercice) => {
    if (!q.generator_key || q.attendu == null) return { question: q, retiree: false };
    try {
        const gen = generateExercice(q.attendu, q.generator_key);
        return gen
            ? { question: { ...gen, attendu: gen.attendu ?? q.attendu, generator_key: q.generator_key }, retiree: true }
            : { question: q, retiree: false };
    } catch {
        return { question: q, retiree: false };
    }
};

/**
 * Les questions du rejeu, au format du moteur Quiz pour un défi
 * (EspaceEleve.handleStartDefi) : une partie, des clés locales `rejeu_i`, la
 * provenance (attendu, generator_key) pour le corps du défi. Le quiz entier,
 * même au-delà de 8 questions, dans l'ordre de la séance. `verdictSeul` : une
 * question reprise à l'identique alors que le prof masque la correction.
 */
export const questionsDuRejeu = (session, generateExercice) => {
    const label = `Rejeu : ${session?.titre || ''}`;
    const correctionMasquee = session?.correction_visible !== true;
    const questions = {};
    (session?.questions || []).forEach((sq, i) => {
        const { question: q, retiree } = retirerAuSort(sq.banque_question || sq, generateExercice);
        questions[`rejeu_${i}`] = {
            id: `rejeu_${i}`,
            type_label: label,
            type_question: q.type,
            texte: q.texte,
            contenu: q.contenu,
            bonne_reponse: resolveBonneReponse(q.contenu),
            elements_additionnels: q.elements_additionnels,
            question_num: i + 1,
            attendu: q.attendu ?? null,
            generator_key: q.generator_key ?? null,
            ...(!retiree && correctionMasquee ? { verdictSeul: true } : {}),
        };
    });
    return Object.keys(questions).length ? { [label]: questions } : {};
};

/**
 * La correction SUR PLACE (la question marque juste, faux et attendu) d'une
 * réponse fausse, en mode correction visible, une fois « Vérifier » cliqué.
 * Une question `verdictSeul` n'a que la barre de verdict (juste ou faux, XP) :
 * la réponse attendue ne s'affiche pas.
 */
export const correctionSurPlace = (question, { correctionVisible, revealed, verdict }) => (
    !!correctionVisible && !!revealed && !verdict && !question?.verdictSeul
);
